import { FormGroup } from '@angular/forms';
import type { FormField } from '../../interfaces/form.interface';
import type { DropdownData } from '../../interfaces/input.interface';
import { DropdownFilterExecutor } from './dropdown-filter.executor';
import { DropdownTransformExecutor } from './dropdown-transform.executor';
import { FieldUpdateExecutor } from './field-update.executor';
import { FormFieldFinder } from './form-field.finder';
import { FormOperationState } from './form-operation-state';

/** Orchestrates field-level dynamic logic in a deterministic order. */
export class FieldLogicExecutor<T> {
    private pendingChangedField: string | null | undefined;

    private currentExecution: Promise<void> | null = null;

    constructor(
        private readonly fieldFinder: FormFieldFinder<T>,
        private readonly model: FormGroup,
        private readonly transformExecutor: DropdownTransformExecutor<T>,
        private readonly filterExecutor: DropdownFilterExecutor<T>,
        private readonly updateExecutor: FieldUpdateExecutor<T>,
        private readonly state: FormOperationState,
    ) {}

    public execute(changedField: string | null): Promise<void> {
        this.pendingChangedField = this.pendingChangedField === undefined
            ? changedField
            : null;

        if (this.currentExecution) {
            return this.currentExecution;
        }

        this.currentExecution = this.drainQueue()
            .finally(() => {
                this.currentExecution = null;
            });

        return this.currentExecution;
    }

    private async drainQueue(): Promise<void> {
        try {
            while (this.pendingChangedField !== undefined) {
                const changedField = this.pendingChangedField;

                this.pendingChangedField = undefined;
                await this.executeAsync(changedField);
            }
        } finally {
            this.pendingChangedField = undefined;
        }
    }

    private async executeAsync(changedField: string | null): Promise<void> {
        const fields = this.fieldFinder.getFields().filter(field => this.shouldExecuteFieldLogic(field, changedField));

        for (const field of fields) {
            await this.executeFieldLogic(field, changedField);
        }
    }

    private async executeFieldLogic(field: FormField<T>, changedField: string | null): Promise<void> {
        if (field.input?.dropdownConfiguration?.transform) {
            this.transformExecutor.applyTransforms(field);
        }

        if (field.input?.dropdownConfiguration?.filter) {
            const selectedValueChanged = this.filterExecutor.applyFilter(field, changedField);

            if (selectedValueChanged && field.name) {
                void this.execute(field.name);
            }
        }

        if (field.input?.dropdownConfiguration?.swap) {
            field.input.dropdownConfiguration.swap(field);
        }

        if (field.turnVisible) {
            field.turnVisible(field);
        }

        if (typeof field.input?.dropdownConfiguration?.appendData === 'function') {
            await field.input.dropdownConfiguration.appendData(field);
        }

        this.applyDynamicInputType(field, changedField);

        this.updateExecutor.applyUpdateValue(field);
    }

    private shouldExecuteFieldLogic(field: FormField<T>, changedField: string | null): boolean {
        return this.shouldExecuteFilter(field, changedField) ||
            this.hasTransform(field) ||
            this.hasUpdateValue(field) ||
            this.hasAppendData(field, changedField) ||
            this.shouldExecuteDynamicInputType(field, changedField) ||
            Boolean(field.input?.dropdownConfiguration?.swap) ||
            Boolean(field.turnVisible);
    }

    private shouldExecuteFilter(field: FormField<T>, changedField: string | null): boolean {
        return Boolean(
            field.input?.dropdownConfiguration?.filter &&
            field.input.data &&
            (changedField === null || field.vinculatedFields === undefined || field.vinculatedFields.includes(changedField)),
        );
    }

    private shouldExecuteDynamicInputType(field: FormField<T>, changedField: string | null): boolean {
        const config = field.input?.dynamicTypeConfiguration;

        if (!config) {
            return false;
        }

        if (changedField === null || config.dependsOn === undefined) {
            return true;
        }

        const dependencies = Array.isArray(config.dependsOn) ? config.dependsOn : [config.dependsOn];

        return dependencies.includes(changedField);
    }

    private applyDynamicInputType(field: FormField<T>, changedField: string | null): void {
        const config = field.input?.dynamicTypeConfiguration;

        if (!config || !this.shouldExecuteDynamicInputType(field, changedField)) {
            return;
        }

        const resolvedType = config.resolver({
            field,
            model: this.model,
            changedField,
            currentValue: this.model.get(field.name)?.value,
            defaultType: config.defaultType,
        });

        field.input.type = resolvedType;
    }

    private hasTransform(field: FormField<T>): boolean {
        return Boolean(field.input?.dropdownConfiguration?.transform && (field.input.data as DropdownData<T>));
    }

    private hasUpdateValue(field: FormField<T>): boolean {
        return Boolean(field.updateValue && !this.state.isProgrammaticChange);
    }

    private hasAppendData(field: FormField<T>, changedField: string | null): boolean {
        return Boolean(
            typeof field.input?.dropdownConfiguration?.appendData === 'function' &&
            (changedField === null || field.vinculatedFields === undefined || field.vinculatedFields.includes(changedField)),
        );
    }
}
